import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Import translations
import { site } from './translations/site';
import { common } from './translations/common';
import { navigation } from './translations/navigation';
import { home } from './translations/pages/home';
import { about } from './translations/pages/about';
import { contact } from './translations/pages/contact';
import { services } from './translations/services';
import { solutions as pageSolutions } from './translations/pages/solutions';
import { solutions } from './translations/solutions';
import { forms } from './translations/forms';
import { cookies } from './translations/cookies';
import { webDevelopment } from './translations/services/webDevelopment';
import { consulting } from './translations/services/consulting';
import { webDesign } from './translations/services/webDesign';
import { webProgramming } from './translations/services/webProgramming';
import { services as pageServices } from './translations/pages/services';

// Force delete old cache that might be using the wrong structure
try {
  localStorage.removeItem('i18nextLng');
} catch (e) {
  console.warn('Could not clear language cache:', e);
}

// Define a deep merge function to correctly preserve nested structures
const deepMerge = (target: Record<string, any>, source: Record<string, any>) => {
  const output = { ...target };
  
  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (source[key] instanceof Object && key in target && target[key] instanceof Object) {
        output[key] = deepMerge(target[key], source[key]);
      } else {
        output[key] = source[key];
      }
    }
  }
  
  return output;
};

const resources = {
  is: {
    translation: {
      site: site.is,
      common: common.is,
      nav: navigation.is,
      home: home.is,
      about: about.is,
      contact: contact.is,
      services: {
        ...services.is,
        ...pageServices.is,
        webDevelopment: webDevelopment.is,
        webDesign: webDesign.is,
        webProgramming: webProgramming.is,
        consulting: consulting.is
      },
      solutions: {
        ...pageSolutions.is,
        ...solutions.is
      },
      forms: forms.is,
      cookies: cookies.is
    }
  },
  en: {
    translation: {
      site: site.en,
      common: common.en,
      nav: navigation.en,
      home: home.en,
      about: about.en,
      contact: contact.en,
      services: {
        ...services.en,
        ...pageServices.en,
        webDevelopment: webDevelopment.en,
        webDesign: webDesign.en,
        webProgramming: webProgramming.en,
        consulting: consulting.en
      },
      solutions: {
        ...pageSolutions.en,
        ...solutions.en
      },
      forms: forms.en,
      cookies: cookies.en
    }
  },
  es: {
    translation: {
      site: site.es,
      common: common.es,
      nav: navigation.es,
      home: home.es,
      about: about.es,
      contact: contact.es,
      services: {
        ...services.es,
        ...pageServices.es,
        webDevelopment: webDevelopment.es,
        webDesign: webDesign.es,
        webProgramming: webProgramming.es,
        consulting: consulting.es
      },
      solutions: {
        ...pageSolutions.es,
        ...solutions.es
      },
      forms: forms.es,
      cookies: cookies.es
    }
  },
  da: {
    translation: {
      site: site.da,
      common: common.da,
      nav: navigation.da,
      home: home.da,
      about: about.da,
      contact: contact.da,
      services: {
        ...services.da,
        ...pageServices.da,
        webDevelopment: webDevelopment.da,
        webDesign: webDesign.da,
        webProgramming: webProgramming.da,
        consulting: consulting.da
      },
      solutions: {
        ...pageSolutions.da,
        ...solutions.da
      },
      forms: forms.da,
      cookies: cookies.da
    }
  }
};

// Map domains to language codes
const domainLanguageMap: { [key: string]: string } = {
  'vefsida.is': 'is',
  'vefsida.io': 'en',
  'vefsida.es': 'es',
  'vefsida.dk': 'da',
  'localhost': 'is' // Default for local development
};

// Custom language detector that forces language based on domain
const customDomainDetector = {
  name: 'customDomainDetector',
  lookup() {
    const hostname = window.location.hostname;
    return domainLanguageMap[hostname] || 'is';
  },
  cacheUserLanguage() {
    // No caching - we always want to use domain-based language
  }
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'is',
    lng: customDomainDetector.lookup(),
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['customDomainDetector'],
      caches: [] // Disable caching
    },
    // Force reload to avoid stale references
    load: 'currentOnly',
    react: {
      useSuspense: false,
    },
  });

// Force reload translations after initialization
setTimeout(() => {
  i18n.reloadResources().then(() => {
    console.log('Translations forcefully reloaded');
    console.log('WebDevelopment translations object:', i18n.t('services.webDevelopment', { returnObjects: true }));
    console.log('WebProgramming translations object:', i18n.t('services.webProgramming', { returnObjects: true }));
    console.log('WebProgramming sections object:', i18n.t('services.sections.webProgramming', { returnObjects: true }));
    console.log('WebDesign translations object:', i18n.t('services.webDesign', { returnObjects: true }));
    console.log('Consulting translations object:', i18n.t('services.consulting', { returnObjects: true }));
    console.log('Solutions AI title:', i18n.t('solutions.ai.title'));
  }).catch(err => {
    console.error('Failed to reload translations:', err);
  });
}, 100);

export default i18n;